import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { RadioButton } from "primereact/radiobutton";
import { Calendar } from "primereact/calendar";
import { InputSwitch } from "primereact/inputswitch";
import { FileUpload } from "primereact/fileupload";
import { InputTextarea } from "primereact/inputtextarea";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { Divider } from "primereact/divider";
import { Button } from "primereact/button";
import MapDialog from "../../GenerateForms/MapDialog";
import { MultiSelect } from "primereact/multiselect";

const UserCreatedFormPreviewDialog = ({ visible, onHide, form }) => {
  const [groupedFields, setGroupedFields] = useState({});
  const [mapDialogVisible, setMapDialogVisible] = useState(false);
  const [selectedMapField, setSelectedMapField] = useState(null);
  const [selectedCoordinates, setSelectedCoordinates] = useState({});

  useEffect(() => {
    if (form && form.properties && typeof form.properties === "object") {
      const groupFields = () => {
        const grouped = {};
        Object.values(form.properties).forEach((field) => {
          const group = field.group || "ungrouped";
          if (!grouped[group]) {
            grouped[group] = [];
          }
          grouped[group].push(field);
        });
        setGroupedFields(grouped);
      };

      groupFields();
    }
  }, [form]);

  const renderField = (field) => {
    switch (field.type) {
      case "text":
      case "email":
      case "number":
        return (
          <InputText
            type={field.type}
            maxLength={field.maxLength || undefined}
            min={field.min || undefined}
            max={field.max || undefined}
            placeholder={field.key}
            className="w-full mb-3"
          />
        );
      case "geolocation": {
        const coords = selectedCoordinates[field.name];
        const label = coords
          ? Array.isArray(coords)
            ? coords
                .map((c) => (typeof c === "number" ? c.toFixed(6) : c))
                .join(", ")
            : String(coords)
          : "Select Coordinates";

        return (
          <Button
            label={coords ? `Selected: ${label}` : "Select Coordinates"}
            icon="pi pi-map-marker"
            onClick={() => {
              setSelectedMapField(field);
              setMapDialogVisible(true);
            }}
            className="w-full mb-3"
            style={{
              backgroundColor: "#609856",
              color: "#fff",
              border: "none",
            }}
          />
        );
      }

      case "multiselect":
        return (
          <MultiSelect
            value={field.value || []}
            options={(field.options || []).map((o) => ({
              label: o.option,
              value: o.value,
            }))}
            placeholder={field.key}
            className="w-full mb-3"
            display="chip"
          />
        );
      case "checkbox":
        return field.options.map((option, idx) => (
          <div key={idx} className="p-field-checkbox mb-2">
            <Checkbox
              inputId={`field-${field.name}-option-${idx}`}
              name={field.name}
              value={option.value}
              className="mr-2 ml-5"
            />
            <label
              htmlFor={`field-${field.name}-option-${idx}`}
              className="font-medium"
            >
              {option.option}
            </label>
          </div>
        ));
      case "dropdown":
        return (
          <Dropdown
            value={field.value}
            options={field.options.map((option) => ({
              label: option.option,
              value: option.value,
            }))}
            placeholder={`Select ${field.name}`}
            className="w-full mb-5"
            filter
          />
        );
      case "radio":
        return field.options.map((option, idx) => (
          <div key={idx} className="p-field-radiobutton">
            <RadioButton
              inputId={`field-${field.name}-option-${idx}`}
              name={field.name}
              value={option.value}
              className="mr-2 mb-1 ml-5"
            />
            <label
              htmlFor={`field-${field.name}-option-${idx}`}
              className="font-medium"
            >
              {option.option}
            </label>
          </div>
        ));
      case "date":
        return (
          <Calendar
            placeholder={`Select ${field.name}`}
            className="w-full mb-3"
          />
        );
      case "inputswitch":
        return (
          <InputSwitch
            placeholder={`Select ${field.name}`}
            className="mb-3 ml-5"
          />
        );
      case "fileupload":
        return (
          <FileUpload
            name={field.name}
            accept={field.fileType === "all" ? "*" : `${field.fileType}/*`}
            className="w-full mb-3 ml-5"
            mode="basic"
            chooseLabel="Choose"
            chooseOptions={{
              className: "p-button p-component",
              style: {
                backgroundColor: "#e29b45",
                color: "#fff",
                border: "none",
              },
            }}
          />
        );
      case "textarea":
        return (
          <InputTextarea
            name={field.name}
            required={field.required}
            className="w-full mb-3"
            mode="basic"
            autoResize
            rows={5}
            cols={30}
          />
        );
      default:
        return null;
    }
  };

  const renderGroupedFields = () => {
    return Object.entries(groupedFields).map(([group, fields]) => {
      const fieldsPerRow = fields[0].fieldsPerRow || 1;
      const flexBasis = `calc(${100 / fieldsPerRow}% - 1rem)`;

      return (
        <React.Fragment key={group}>
          {group !== "ungrouped" && fields[0].renderHeader !== null && (
            <h3 style={{ width: "100%", marginBottom: "1rem" }}>{group}</h3>
          )}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "1rem",
              marginBottom: "1rem",
            }}
          >
            {fields.map((field, index) => (
              <div
                key={index}
                style={{
                  flex: group === "ungrouped" ? "1 1 100%" : `1 1 ${flexBasis}`,
                  minWidth: group === "ungrouped" ? "100%" : "200px",
                }}
              >
                <label className="block text-900 font-bold mb-2 mt-3">
                  {field.key}
                </label>
                {renderField(field)}
              </div>
            ))}
          </div>
          {group !== "ungrouped" && <Divider />}
        </React.Fragment>
      );
    });
  };

  if (!form) {
    return null;
  }

  return (
    <Dialog
      header="Form Preview"
      visible={visible}
      style={{
        width: window.innerWidth > 768 ? "80vw" : "95vw",
      }}
      onHide={onHide}
      className="form-preview-dialog"
    >
      <div className="p-grid p-dir-col table-page">
        <div className="p-col title">
          <h1
            style={{
              fontSize: window.innerWidth > 768 ? "2rem" : "1.5rem",
              wordBreak: "break-word",
              overflowWrap: "break-word",
              maxWidth: "100%",
            }}
          >
            {form.title}
          </h1>
          <div className="title-bar"></div>
        </div>
        <div className="table-container">
          <ReactMarkdown rehypePlugins={[rehypeRaw]}>
            {form.description}
          </ReactMarkdown>
          <Divider />
          {renderGroupedFields()}
        </div>
        {mapDialogVisible && (
          <MapDialog
            visible={mapDialogVisible}
            onHide={() => setMapDialogVisible(false)}
            onSave={(coords) => {
              if (!selectedMapField) return;
              setSelectedCoordinates((prev) => ({
                ...prev,
                [selectedMapField.name]: coords,
              }));
              setMapDialogVisible(false);
            }}
            initialValue={selectedCoordinates[selectedMapField?.name]}
          />
        )}
      </div>
    </Dialog>
  );
};

export default UserCreatedFormPreviewDialog;
